import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInventory } from '../../contexts/InventoryContext';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiTag, FiCamera, FiTrash2, FiImage, FiUploadCloud } = FiIcons;

const AddSalesListingModal = ({ isOpen, onClose, productId, existingListing = null }) => {
  const { addSalesListing, updateSalesListing } = useInventory();
  
  const [formData, setFormData] = useState({
    platform: 'eBay',
    url: '',
    price: '',
    grade: 'New',
    comments: '',
    status: 'active',
    images: []
  });

  // Camera & Image States
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const platforms = [
    'eBay', 'Facebook Marketplace', 'OfferUp', 'Craigslist', 'Amazon', 'Mercari', 'Other'
  ];

  const grades = [
    'New', 'Open Box', 'Used - Like New', 'Used - Very Good', 'Used - Good', 'Used - Fair', 'For Parts/Repair', 'Refurbished'
  ];

  const statuses = [
    { value: 'active', label: 'Active (Locks Inventory)' },
    { value: 'pending_shipping', label: 'Pending Shipping (Locks Inventory)' },
    { value: 'waiting_for_pickup', label: 'Waiting for Pickup (Locks Inventory)' },
    { value: 'sold', label: 'Sold (Removes Inventory)' },
    { value: 'canceled', label: 'Canceled (Unlocks Inventory)' },
    { value: 'paused', label: 'Paused' }
  ];

  useEffect(() => {
    if (existingListing) {
      setFormData({
        platform: existingListing.platform,
        url: existingListing.url || '',
        price: existingListing.price.toString(),
        grade: existingListing.grade || 'New',
        comments: existingListing.comments || '',
        status: existingListing.status || 'active',
        images: existingListing.images || []
      });
    }
  }, [existingListing]);

  // Cleanup camera stream on unmount or modal close
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  // NEW: Effect to attach stream to video element once it renders
  useEffect(() => {
    if (showCamera && cameraStream && videoRef.current) {
      console.log("Attaching stream to video element");
      videoRef.current.srcObject = cameraStream;
      videoRef.current.play().catch(e => console.error("Error playing video:", e));
    }
  }, [showCamera, cameraStream]);

  const startCamera = async () => {
    try {
      console.log("Requesting camera access...");
      // Try environment facing first, fallback to user (selfie) if needed
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ 
          video: { facingMode: 'environment' } 
        });
      } catch (err) {
        console.warn("Environment camera failed, trying default...", err);
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      
      setCameraStream(stream);
      setShowCamera(true);
      // Note: We do NOT set srcObject here anymore. The useEffect matches them up.
    } catch (err) {
      console.error("Error accessing camera:", err);
      toast.error("Could not access camera. Please check permissions.");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      
      // Check if video is actually playing
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        toast.error("Camera is not ready yet. Please wait a moment.");
        return;
      }

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw current frame
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert to blob for upload
      canvas.toBlob(async (blob) => {
        if (blob) {
          stopCamera(); // Stop stream immediately to freeze UI
          await uploadImage(blob);
        } else {
          toast.error("Failed to capture image");
        }
      }, 'image/jpeg', 0.8);
    }
  };

  const uploadImage = async (fileOrBlob) => {
    setUploadingImage(true);
    try {
      // Generate unique path
      const fileName = `${productId}/${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
      
      const { data, error } = await supabase.storage
        .from('listing-images')
        .upload(fileName, fileOrBlob, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get Public URL
      const { data: { publicUrl } } = supabase.storage
        .from('listing-images')
        .getPublicUrl(fileName);

      // Update State
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, publicUrl]
      }));
      
      toast.success('Photo added successfully');
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload photo: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const listingData = {
      platform: formData.platform,
      url: formData.url,
      price: parseFloat(formData.price),
      grade: formData.grade,
      comments: formData.comments,
      status: formData.status,
      images: formData.images
    };

    if (existingListing) {
      updateSalesListing(productId, existingListing.id, listingData);
    } else {
      addSalesListing(productId, listingData);
    }
    onClose();
    
    // Reset form
    setFormData({
      platform: 'eBay',
      url: '',
      price: '',
      grade: 'New',
      comments: '',
      status: 'active',
      images: []
    });
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {existingListing ? 'Edit Sales Listing' : 'Add Sales Listing'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              
              {/* Sales ID Display */}
              {existingListing && existingListing.sales_id && (
                <div className="bg-blue-50 p-3 rounded-lg flex items-center">
                  <SafeIcon icon={FiTag} className="h-5 w-5 text-blue-600 mr-2" />
                  <div>
                    <p className="text-xs text-blue-600 font-medium">Sales ID (Auto-Generated)</p>
                    <p className="text-sm font-bold text-blue-900">{existingListing.sales_id}</p>
                  </div>
                </div>
              )}

              {/* Photo Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">
                    Listing Photos
                  </label>
                  {!showCamera && (
                    <button
                      type="button"
                      onClick={startCamera}
                      disabled={uploadingImage}
                      className="flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiCamera} className="h-4 w-4 mr-2" />
                      Take Photo
                    </button>
                  )}
                </div>

                {/* Camera Overlay */}
                <AnimatePresence>
                  {showCamera && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="relative bg-black rounded-lg overflow-hidden mb-4"
                    >
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted
                        className="w-full h-64 object-cover"
                      />
                      <canvas ref={canvasRef} className="hidden" />
                      
                      <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent flex justify-center space-x-4">
                        <button
                          type="button"
                          onClick={capturePhoto}
                          className="h-12 w-12 bg-white rounded-full border-4 border-gray-300 flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
                          aria-label="Capture"
                        >
                          <div className="h-10 w-10 bg-white rounded-full border-2 border-black" />
                        </button>
                        <button
                          type="button"
                          onClick={stopCamera}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-red-600/80 hover:bg-red-700 px-3 py-1 rounded-lg text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Image Gallery */}
                {formData.images.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-2">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
                        <img src={img} alt={`Listing ${index + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                          >
                            <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  !showCamera && (
                    <div className="flex items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      <div className="text-center text-gray-500">
                        <SafeIcon icon={FiImage} className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">No photos added yet</p>
                      </div>
                    </div>
                  )
                )}
                
                {uploadingImage && (
                  <div className="flex items-center justify-center p-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Uploading photo...
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Platform *
                  </label>
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {platforms.map(platform => (
                      <option key={platform} value={platform}>
                        {platform}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Listed Price *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      required
                      step="0.01"
                      min="0"
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Product Grade *
                  </label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {grades.map(grade => (
                      <option key={grade} value={grade}>
                        {grade}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments / Notes
                </label>
                <textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any issues, defects, or specific notes about this listing..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Listing URL
                </label>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploadingImage}
                  className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 disabled:opacity-50"
                >
                  <SafeIcon icon={FiSave} className="h-4 w-4 mr-2" />
                  {existingListing ? 'Update' : 'Add'} Listing
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default AddSalesListingModal;